import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ProjectLayout from "../layouts/ProjectLayout.jsx";
import ProjectPage from "../components/ProjectPage.jsx";
import { getAllProjectsWithTags } from "../services/azureProjectsService.js";
import { useAuth } from "../hooks/useAuth";
import { canEdit, isFreelancer, canAccess } from "../utils/permissions";
import { useFreelancerProjects } from "../hooks/useFreelancerProjects";

const ProjectPageWrapper = ({ employees, defaultTab = "info" }) => {
  const { projectSlug, projectCode } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [loading, setLoading] = useState(true);

  // Freelancer checks
  const isFreelancerUser = isFreelancer(user);
  const { canAccessProject } = useFreelancerProjects(user);

  const showControlling = canEdit(user, 'controlling');
  const showPendenzen = canAccess(user, 'pendenzen');
  const showPlaene = canAccess(user, 'plaene');
  const showSitzungen = canAccess(user, 'sitzungen');
  // Reiter "Website" nur, wenn die GL für dieses Projekt eine externe
  // Projektwebsite aktiviert hat (US-WEBP-001).
  const showWebsite = canAccess(user, 'projektwebsite') && project?.projektwebsite === true;
  // Freelancers have read-only access to project pages (except BKP costs)
  const isProjectReadOnly = isFreelancerUser;

  useEffect(() => {
    loadProject();
  }, [projectSlug, projectCode]);

  // Update active tab when defaultTab prop changes
  useEffect(() => {
    if (defaultTab) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab]);

  const loadProject = async () => {
    try {
      setLoading(true);
      const projects = await getAllProjectsWithTags("project");

      // Get project code from either projectSlug or projectCode param
      let code;
      if (projectCode) {
        code = projectCode;
      } else if (projectSlug) {
        code = projectSlug.split("-")[0];
      }

      const found = projects.find((p) => p.code === code);

      if (found) {
        // Check if freelancer has access to this project
        if (isFreelancerUser && !canAccessProject(found.code)) {
          console.warn("Freelancer access denied for project:", found.code);
          navigate("/main-projects");
          return;
        }
        setProject(found);
      } else {
        navigate("/main-projects");
      }
    } catch (error) {
      console.error("Load error:", error);
      navigate("/main-projects");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Projekt wird geladen...</div>;
  if (!project) return null;

  // Generate slug from project code and title
  const getProjectSlug = (proj) => {
    const titleSlug = proj.title
      ?.toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "") || "";
    return `${proj.code}-${titleSlug}`;
  };

  const slug = getProjectSlug(project);

  // El Gatoodle-Tab (US-GATO-001): ausschliesslich für dieses eine Projekt.
  const showGatoodle = slug === "025-el-gato-muerto";

  const navigationItems = [
    {
      id: "info",
      label: "Übersicht",
      onClick: () => navigate(`/projects/${slug}/overview`),
      active: activeTab === "info",
    },
    {
      id: "netzwerk",
      label: "Netzwerk",
      onClick: () => navigate(`/projects/${slug}/network`),
      active: activeTab === "netzwerk",
    },
    // Controlling tab - only visible for Admin/PL
    ...(showControlling ? [{
      id: "controlling",
      label: "Controlling",
      onClick: () => navigate(`/projects/${slug}/controlling`),
      active: activeTab === "controlling",
    }] : []),
    // Ansätze tab - only visible for Admin/PL
    ...(showControlling ? [{
      id: "ansaetze",
      label: "Ansätze",
      onClick: () => navigate(`/projects/${slug}/rates`),
      active: activeTab === "ansaetze",
    }] : []),
    // Pendenzen tab - visible for Admin/PL/Team (US-PEND-001). Öffnet die
    // Pendenzenliste in der Main-Page (Pendenzen-Shell), nicht in der Projektseite.
    ...(showPendenzen ? [{
      id: "pendenzen",
      label: "Pendenzen",
      onClick: () => navigate(`/main-pendenzen/projekte/${encodeURIComponent(project.code)}`),
      active: false,
    }] : []),
    // Pläne-Tab - visible für Admin/PL/Team (US-PLAN-001)
    ...(showPlaene ? [{
      id: "plaene",
      label: "Pläne",
      onClick: () => navigate(`/projects/${slug}/plaene`),
      active: activeTab === "plaene",
    }] : []),
    // Sitzungen-Tab - visible für Admin/PL/Team (US-SITZ-001)
    ...(showSitzungen ? [{
      id: "sitzungen",
      label: "Sitzungen",
      onClick: () => navigate(`/projects/${slug}/sitzungen`),
      active: activeTab === "sitzungen",
    }] : []),
    // Website-Tab - nur wenn GL die externe Projektwebsite aktiviert hat (US-WEBP-001)
    ...(showWebsite ? [{
      id: "website",
      label: "Website",
      onClick: () => navigate(`/projects/${slug}/website`),
      active: activeTab === "website",
    }] : []),
    // El Gatoodle-Tab - nur für Projekt "025-el-gato-muerto" (US-GATO-001)
    ...(showGatoodle ? [{
      id: "gatoodle",
      label: "El Gatoodle",
      onClick: () => navigate(`/projects/${slug}/gatoodle`),
      active: activeTab === "gatoodle",
    }] : []),
    {
      id: "kosten",
      label: "Kosten",
      onClick: () => navigate(`/kosten?project=${project.code}`),
      active: false,
    },
  ];

  return (
    <ProjectLayout project={project} navigationItems={navigationItems}>
      <ProjectPage
        project={project}
        employees={employees}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        readOnly={isProjectReadOnly}
      />
    </ProjectLayout>
  );
};

export default ProjectPageWrapper;
